const React = require('react');

const { useMemo } = React;

const internals = {};

internals.resolveComponentModule = (moduleValue, exportName) => [
    moduleValue,
    moduleValue?.default,
    moduleValue?.[exportName],
    moduleValue?.default?.default,
    moduleValue?.default?.[exportName]
].find((candidate) => typeof candidate === 'function');

internals.describeComponentModule = (moduleValue = {}) => {

    if (!moduleValue) {
        return String(moduleValue);
    }

    return JSON.stringify({
        type: typeof moduleValue,
        defaultType: typeof moduleValue.default,
        keys: Object.keys(moduleValue),
        defaultKeys: moduleValue.default && typeof moduleValue.default === 'object'
            ? Object.keys(moduleValue.default)
            : []
    });
};

internals.getAutocompleteField = () => {

    const AutocompleteFieldModule = require('./AutocompleteField');
    const AutocompleteField = internals.resolveComponentModule(AutocompleteFieldModule, 'AutocompleteField');

    if (typeof AutocompleteField !== 'function') {
        throw new Error(`PlaceField could not resolve AutocompleteField component from ./AutocompleteField. Module shape: ${internals.describeComponentModule(AutocompleteFieldModule)}`);
    }

    return AutocompleteField;
};

internals.PLACE_TYPES = [
    'locality',
    'sublocality',
    'neighborhood',
    'administrative_area_level_3',
    'colloquial_area'
];

internals.isOrgLike = (formProps = {}) => !!(
    formProps.isCompany
    || formProps.isCommunity
    || formProps.isSchoolOnline
);

internals.resolveLabel = ({
    field = {},
    formProps = {},
    label
}) => {

    if (internals.isOrgLike(formProps) && field.orgLikeLabel) {
        return field.orgLikeLabel;
    }

    return label || field.label;
};

internals.buildEmptyPlaceValue = (field = {}) => ({
    id: null,
    placeId: null,
    name: null,
    ...(field.includeOnCampus ? {
        onCampus: field.defaultOnCampus ?? null
    } : {})
});

internals.buildPlaceValue = ({
    field = {},
    option
}) => {

    if (!option) {
        return internals.buildEmptyPlaceValue(field);
    }

    return {
        id: option.reference ? null : (option.id ?? null),
        placeId: option.place_id || option.placeId || null,
        name: option.description || option.name || null,
        ...(field.includeOnCampus ? {
            onCampus: field.defaultOnCampus ?? null
        } : {})
    };
};

internals.getOptionValue = (option) => option?.place_id || option?.placeId || option?.id || option?.description || option?.name || '';
internals.getOptionLabel = (option) => option?.description || option?.name || '';
internals.getSelectedValue = (value) => value?.placeId || value?.id || value?.name || '';

internals.normalizePlaceOption = (option = {}) => {

    if (!option || typeof option !== 'object') {
        return option;
    }

    if (option.description || option.place_id) {
        return option;
    }

    return {
        ...option,
        description: option.name,
        place_id: option.placeId
    };
};

internals.getStaticOptions = ({
    field = {},
    formProps = {}
}) => {

    if (Array.isArray(field.staticOptions)) {
        return field.staticOptions.map(internals.normalizePlaceOption);
    }

    if (typeof field.staticOptionsSource === 'string' && Array.isArray(formProps[field.staticOptionsSource])) {
        return formProps[field.staticOptionsSource].map(internals.normalizePlaceOption);
    }

    return [];
};

internals.getGoogleAutocompleteService = () => {

    if (typeof window === 'undefined' || !window.google?.maps?.places?.AutocompleteService) {
        return null;
    }

    return new window.google.maps.places.AutocompleteService();
};

internals.buildGooglePlacesDataSource = ({
    field = {},
    formProps = {}
}) => {

    const autocompleteService = internals.getGoogleAutocompleteService();
    const staticOptions = internals.getStaticOptions({
        field,
        formProps
    });
    const types = field.placeTypes || formProps.placeTypes || internals.PLACE_TYPES;

    return ({ query }) => new Promise((resolve) => {

        if (!query || !query.length || !autocompleteService || typeof window === 'undefined' || !window.google) {
            resolve(staticOptions);
            return;
        }

        autocompleteService.getPlacePredictions({
            input: query,
            location: new window.google.maps.LatLng(0, 0),
            radius: 0,
            types
        }, (data = []) => {
            resolve(staticOptions.concat(data || []));
        });
    });
};

module.exports = function PlaceField(props) {

    const AutocompleteField = internals.getAutocompleteField();

    const {
        field = {},
        fieldName = field.name,
        formProps = {},
        label = field.label,
        methods,
        textFieldProps = {},
        ...autocompleteProps
    } = props;

    const dataSource = useMemo(() => {

        if (typeof field.dataSource === 'function') {
            return field.dataSource;
        }

        return internals.buildGooglePlacesDataSource({
            field,
            formProps
        });
    }, [field, formProps]);
    const clearValue = useMemo(() => Object.prototype.hasOwnProperty.call(field, 'clearValue')
        ? field.clearValue
        : internals.buildEmptyPlaceValue(field), [field]);
    const resolvedLabel = internals.resolveLabel({
        field,
        formProps,
        label
    });

    return <AutocompleteField
        {...autocompleteProps}
        clearValue={clearValue}
        dataSource={dataSource}
        field={{
            exactOptionRequired: true,
            suppressEmptyState: true,
            ...field
        }}
        fieldName={fieldName}
        fieldValueFromOption={(option) => internals.buildPlaceValue({
            field,
            option
        })}
        getOptionLabel={internals.getOptionLabel}
        getOptionValue={internals.getOptionValue}
        getSelectedValue={internals.getSelectedValue}
        label={resolvedLabel}
        methods={methods}
        suppressEmptyState={field.suppressEmptyState ?? true}
        textFieldName={field.textFieldName || `${fieldName}Text`}
        textFieldProps={{
            placeholder: field.placeholder || 'Type to search...',
            ...textFieldProps
        }}
        throttleMs={field.throttleMs || 250}
    />;
};

module.exports.internals = internals;
